import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MessageSquareText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getUnseenFeedback, markFeedbackSeen } from '@/lib/feedback-api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'

const FEEDBACK_PATH = '/admin/feedback'

// One notice for all feedback that arrived since it was last closed. Closing it
// marks exactly the shown batch as seen, so later feedback triggers a new notice.
export function FeedbackNoticeDialog() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const onFeedbackPage = location.pathname.startsWith(FEEDBACK_PATH)

  const unseenQuery = useQuery({
    queryKey: queryKeys.adminUnseenFeedback,
    queryFn: getUnseenFeedback,
    refetchInterval: 5 * 60 * 1000,
  })

  const confirmMutation = useMutation({
    mutationFn: async (target: 'later' | 'page') => {
      await markFeedbackSeen(unseenQuery.data?.latest_created_at ?? undefined)
      return target
    },
    onSuccess: (target) => {
      queryClient.setQueryData(queryKeys.adminUnseenFeedback, { unseen_count: 0, latest_created_at: null })
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUnseenFeedback })
      if (target === 'page') navigate(FEEDBACK_PATH)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const count = unseenQuery.data?.unseen_count ?? 0
  const open = count > 0 && !onFeedbackPage

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        className="max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5 text-primary" />
            Neues Feedback eingegangen
          </DialogTitle>
          <DialogDescription>
            {count === 1
              ? 'Ein Teilnehmer hat neues Feedback eingemeldet.'
              : `Teilnehmer haben ${count} neue Feedbacks eingemeldet.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => confirmMutation.mutate('later')}
            disabled={confirmMutation.isPending}
          >
            Schließen
          </Button>
          <Button
            type="button"
            onClick={() => confirmMutation.mutate('page')}
            disabled={confirmMutation.isPending}
            autoFocus
          >
            Zum Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
